import React, { useState, useEffect } from "react";
import { Table, Spinner, Alert, Button, Modal, Card } from "react-bootstrap";
import {
  updateUserPublicPlayerId,
  removeUserPublicPlayerId,
  removeEventEntryRoundDetailId,
} from "../util/db";

function EventManageRegistrants({ eventId, roundId, items }) {
  const [registrants, setRegistrants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Manual mapping state
  const [showManualMapping, setShowManualMapping] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [mappingInProgress, setMappingInProgress] = useState(false);

  // Fetch registrants from KP Leagues API
  const fetchRegistrants = async () => {
    if (!roundId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `https://www.kpleagues.com/json_json/event_players.php?round_id=${roundId}`,
        {
          method: "GET",
          headers: {
            "X-API-Key": process.env.REACT_APP_KP_LEAGUES_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();

      if (data.success && data.data) {
        setRegistrants(data.data);
      } else {
        setError("No registrant data found for this round");
      }
    } catch (err) {
      console.error("Error fetching registrants:", err);

      // Check if it's a CORS error
      if (err.message === "Failed to fetch" || err.name === "TypeError") {
        setError(
          "KP Leagues registrant data is not available due to CORS restrictions. This feature works in production but is limited on localhost."
        );
      } else {
        setError(err.message || "Failed to fetch registrants");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrants();
  }, [roundId]);

  // Manual mapping function
  const manualMapPlayer = async (localPlayer, kpPlayer) => {
    setMappingInProgress(true);
    try {
      // Step 1: Validate no existing mappings
      console.log("Validating mapping constraints...");

      // Check if local user is already mapped to a different KP player
      if (
        localPlayer.users_public?.player_id &&
        localPlayer.users_public.player_id !== kpPlayer.player_id
      ) {
        const confirmOverwrite = window.confirm(
          `This user is already mapped to player ID ${localPlayer.users_public.player_id}. ` +
            `Do you want to remap them to ${kpPlayer.player_name} (${kpPlayer.player_id})?`
        );
        if (!confirmOverwrite) {
          return;
        }
      }

      // Check if KP player is already mapped to a different user
      if (kpPlayer.user_id && kpPlayer.user_id !== localPlayer.user_id) {
        alert(
          `Error: KP Leagues player "${kpPlayer.player_name}" is already mapped to user ID ${kpPlayer.user_id}. ` +
            `Please refresh the data and try again.`
        );
        return;
      }

      // Step 2: Check current registrants for conflicts
      const conflictingRegistrant = registrants.find(
        (r) =>
          r.player_id === kpPlayer.player_id &&
          r.user_id &&
          r.user_id !== localPlayer.user_id
      );

      if (conflictingRegistrant) {
        alert(
          `Error: Player "${kpPlayer.player_name}" is already mapped to user ${conflictingRegistrant.user_id}. ` +
            `Please refresh the data and try again.`
        );
        return;
      }

      // Step 3: Map in KP Leagues
      console.log("Mapping in KP Leagues...");
      const kpResponse = await fetch(
        "https://www.kpleagues.com/json_json/map_player_to_user.php",
        {
          method: "POST",
          headers: {
            "X-API-Key": process.env.REACT_APP_KP_LEAGUES_API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            player_id: kpPlayer.player_id,
            user_id: localPlayer.user_id,
          }),
        }
      );

      const kpData = await kpResponse.json();
      if (!kpData.success) {
        throw new Error(kpData.message || "Failed to map in KP Leagues");
      }

      // Step 4: Update local users_public
      console.log("Updating local database...");
      await updateUserPublicPlayerId(localPlayer.user_id, kpPlayer.player_id);

      alert(
        `✅ Successfully mapped ${localPlayer.users_public?.name} to ${kpPlayer.player_name}!\n\n` +
          `User ID: ${localPlayer.user_id} ↔ Player ID: ${kpPlayer.player_id}`
      );

      // Step 5: Refresh data and close modal
      console.log("Refreshing data...");
      fetchRegistrants();
      setShowManualMapping(false);
      setSelectedPlayer(null);
    } catch (err) {
      console.error("Manual mapping error:", err);
      alert(`❌ Mapping Error: ${err.message}`);
    } finally {
      setMappingInProgress(false);
    }
  };

  // Unmap player function
  const handleUnmapPlayer = async (player) => {
    // Confirm unmapping action
    const playerName = player.users_public?.name || "Unknown Player";
    const playerId = player.users_public?.player_id;

    const confirmUnmap = window.confirm(
      `Are you sure you want to unmap ${playerName}?\n\n` +
        `This will:\n` +
        `• Remove Player ID ${playerId} from this user\n` +
        `• Clear the mapping in KP Leagues\n` +
        `• Round detail assignments will remain unchanged\n\n` +
        `This action cannot be undone.`
    );

    if (!confirmUnmap) {
      return;
    }

    setMappingInProgress(true);

    try {
      // Step 1: Unmap in KP Leagues first
      console.log("Unmapping in KP Leagues...");
      const kpResponse = await fetch(
        "https://www.kpleagues.com/json_json/map_player_to_user.php",
        {
          method: "POST",
          headers: {
            "X-API-Key": process.env.REACT_APP_KP_LEAGUES_API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            player_id: playerId,
            user_id: null, // Set to null to unmap
          }),
        }
      );

      const kpData = await kpResponse.json();
      if (!kpData.success) {
        throw new Error(
          `KP Leagues unmapping failed: ${kpData.message || "Unknown error"}`
        );
      }

      // Step 2: Only proceed to local database if KP Leagues succeeded
      console.log("Updating local database...");
      await removeUserPublicPlayerId(player.user_id);

      alert(
        `✅ Successfully unmapped ${playerName}!\n\n` +
          `Player ID ${playerId} has been removed from user ${player.user_id}`
      );

      // Step 3: Refresh data to show updated state
      console.log("Refreshing data...");
      fetchRegistrants();
    } catch (error) {
      console.error("Unmapping error:", error);
      alert(`❌ Unmapping failed: ${error.message}`);
    } finally {
      setMappingInProgress(false);
    }
  };

  // Unmap round detail function
  const handleUnmapRoundDetail = async (player) => {
    // TODO: Implement round detail unmapping functionality
    // Confirm unmapping action
    const playerName = player.users_public?.name || "Unknown Player";
    const roundDetailId = player.round_detail_id;
    const eventEntriesId = player.id; // This is the event_entries_id

    const confirmUnmap = window.confirm(
      `Are you sure you want to unmap round detail for ${playerName}?\n\n` +
        `This will:\n` +
        `• Remove Round Detail ID ${roundDetailId} from event entry ${eventEntriesId}\n` +
        `• Clear the mapping in KP Leagues\n` +
        `• Player mapping will remain unchanged\n\n` +
        `This action cannot be undone.`
    );

    if (!confirmUnmap) {
      return;
    }

    setMappingInProgress(true);

    try {
      // Step 1: Unmap in KP Leagues first
      console.log("Unmapping round detail in KP Leagues...");
      const kpResponse = await fetch(
        "https://www.kpleagues.com/json_json/map_round_detail_id_to_event_entries_id.php",
        {
          method: "POST",
          headers: {
            "X-API-Key": process.env.REACT_APP_KP_LEAGUES_API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            round_detail_id: roundDetailId,
            event_entries_id: null, // Set to null to unmap
          }),
        }
      );

      const kpData = await kpResponse.json();
      if (!kpData.success) {
        throw new Error(
          `KP Leagues round detail unmapping failed: ${
            kpData.message || "Unknown error"
          }`
        );
      }

      // Step 2: Only proceed to local database if KP Leagues succeeded
      console.log("Updating local database...");
      await removeEventEntryRoundDetailId(eventEntriesId);

      alert(
        `✅ Successfully unmapped round detail for ${playerName}!\n\n` +
          `Round Detail ID ${roundDetailId} has been removed from event entry ${eventEntriesId}`
      );

      // Step 3: Refresh data to show updated state
      console.log("Refreshing data...");
      fetchRegistrants();
    } catch (error) {
      console.error("Round detail unmapping error:", error);
      alert(`❌ Round detail unmapping failed: ${error.message}`);
    } finally {
      setMappingInProgress(false);
    }
  };

  // Function to calculate name similarity and sort registrants
  const getSortedRegistrantsByNameMatch = (localPlayerName, registrants) => {
    if (!localPlayerName) return registrants;

    const localName = localPlayerName.toLowerCase().trim();
    const localNameParts = localName.split(/\s+/);

    return registrants
      .map((registrant) => {
        const registrantName = (registrant.player_name || "")
          .toLowerCase()
          .trim();
        const registrantNameParts = registrantName.split(/\s+/);

        let score = 0;
        let matchType = "none";

        // Exact match (highest priority)
        if (localName === registrantName) {
          score = 100;
          matchType = "exact";
        }
        // Check if all local name parts exist in registrant name
        else if (
          localNameParts.every((part) => registrantName.includes(part))
        ) {
          score = 90;
          matchType = "all_parts";
        }
        // Check if all registrant name parts exist in local name
        else if (
          registrantNameParts.every((part) => localName.includes(part))
        ) {
          score = 85;
          matchType = "reverse_all_parts";
        }
        // Partial matches
        else {
          const matchingParts = localNameParts.filter((part) =>
            registrantNameParts.some(
              (rPart) => rPart.includes(part) || part.includes(rPart)
            )
          );

          if (matchingParts.length > 0) {
            score =
              (matchingParts.length /
                Math.max(localNameParts.length, registrantNameParts.length)) *
              70;
            matchType = "partial";
          }
        }

        return {
          ...registrant,
          matchScore: score,
          matchType: matchType,
        };
      })
      .sort((a, b) => b.matchScore - a.matchScore);
  };

  return (
    <div className="event-manage-registrants">
      {/* Header Information Card */}
      <Card
        style={{ borderRadius: "12px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
        className="mb-4"
      >
        <Card.Header
          style={{
            background: "#2D083F",
            color: "white",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Card.Title className="mb-0" style={{ color: "white" }}>
            Event Registrants Management
          </Card.Title>
        </Card.Header>
        <Card.Body
          style={{
            background: "#FFFFFF",
            borderRadius: "0 0 12px 12px",
            padding: "1.5rem",
          }}
        >
          <div className="row">
            <div className="col-md-6">
              <strong>Event ID:</strong> {eventId}
            </div>
            <div className="col-md-6">
              <strong>Round ID:</strong> {roundId}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Event Players Card */}
      <Card
        style={{ borderRadius: "12px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}
        className="mb-4"
      >
        <Card.Header
          style={{
            background: "#2D083F",
            color: "white",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <Card.Title className="mb-0" style={{ color: "white" }}>
            Event Players
          </Card.Title>
        </Card.Header>
        <Card.Body
          style={{
            background: "#FFFFFF",
            borderRadius: "0 0 12px 12px",
            padding: "1.5rem",
            maxHeight: "600px",
            overflowY: "auto",
          }}
        >
          {items && items.length > 0 ? (
            <div style={{ overflowX: "auto" }}>
              <Table striped bordered hover responsive="sm" size="sm">
                <thead>
                  <tr>
                    <th>Player Name</th>
                    <th>Email</th>
                    <th>Player ID / Actions</th>
                    <th>User ID</th>
                    <th>Event Entries ID</th>
                    <th>Round Detail ID</th>
                    <th>Mapping Status</th>
                    <th>Score</th>
                    <th>Advanced Score</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((player, index) => (
                    <tr key={player.id || index}>
                      <td>{player.users_public?.name || "N/A"}</td>
                      <td>{player.users?.email || "N/A"}</td>
                      <td>
                        {player.users_public?.player_id ? (
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-success">
                              ✅ {player.users_public.player_id}
                            </span>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleUnmapPlayer(player)}
                              title="Unmap this player"
                              disabled={mappingInProgress}
                            >
                              🗑️
                            </Button>
                          </div>
                        ) : (
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-warning">⚠️ Not mapped</span>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => {
                                setSelectedPlayer(player);
                                setShowManualMapping(true);
                              }}
                            >
                              Map Player
                            </Button>
                          </div>
                        )}
                      </td>
                      <td>{player.user_id || "N/A"}</td>
                      <td>{player.id || "N/A"}</td>
                      <td>
                        {player.round_detail_id ? (
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-success">
                              ✅ {player.round_detail_id}
                            </span>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleUnmapRoundDetail(player)}
                              title="Unmap round detail"
                              disabled={mappingInProgress}
                            >
                              🗑️
                            </Button>
                          </div>
                        ) : (
                          <span className="text-warning">⚠️ Not mapped</span>
                        )}
                      </td>
                      <td>
                        {(() => {
                          const hasPlayerId = player.users_public?.player_id;
                          const hasRoundDetailId = player.round_detail_id;

                          if (hasPlayerId && hasRoundDetailId) {
                            return (
                              <span className="text-success">✅ Complete</span>
                            );
                          } else if (hasPlayerId && !hasRoundDetailId) {
                            return (
                              <span className="text-info">
                                🔄 User mapped, needs round detail
                              </span>
                            );
                          } else {
                            return (
                              <span className="text-warning">
                                ⚠️ Needs user mapping first
                              </span>
                            );
                          }
                        })()}
                      </td>
                      <td>
                        {player.score !== null && player.score !== undefined
                          ? player.score
                          : "Not scored"}
                      </td>
                      <td>
                        {player.score_adv !== null &&
                        player.score_adv !== undefined
                          ? player.score_adv
                          : "N/A"}
                      </td>
                      <td>
                        {player.is_dnf
                          ? "DNF"
                          : player.is_dropped
                          ? "Dropped"
                          : "Active"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p>No players found for this event.</p>
          )}
        </Card.Body>
      </Card>

      {/* KP Leagues Registrants Card */}
      <Card
        style={{
          borderRadius: "12px",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
        className="mb-4"
      >
        <Card.Header
          style={{
            background: "#2D083F",
            color: "white",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <Card.Title className="mb-0" style={{ color: "white" }}>
              KP Leagues Registrants
            </Card.Title>
            <Button
              variant="outline-light"
              size="sm"
              onClick={fetchRegistrants}
              disabled={loading}
            >
              🔄 Refresh KPL Data
            </Button>
          </div>
        </Card.Header>
        <Card.Body
          style={{
            background: "#FFFFFF",
            borderRadius: "0 0 12px 12px",
            padding: "1.5rem",
            maxHeight: "600px",
            overflowY: "auto",
          }}
        >
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="sr-only">Loading KP Leagues data...</span>
              </Spinner>
              <p className="mt-2">Loading KP Leagues data...</p>
            </div>
          ) : error ? (
            <Alert variant="warning" className="mb-0">
              <strong>Notice:</strong> {error}
              {error.includes("CORS") && (
                <>
                  <br />
                  <small>
                    <strong>For development:</strong> You can test this API
                    endpoint directly in your browser or using a tool like
                    Postman:
                    <br />
                    <code>
                      https://www.kpleagues.com/json_json/event_players.php?round_id=
                      {roundId}
                    </code>
                    <br />
                    Remember to include the X-API-Key header with your API key.
                  </small>
                </>
              )}
            </Alert>
          ) : registrants.length === 0 ? (
            <Alert variant="warning" className="mb-0">
              <strong>No registrants found</strong>
              <br />
              This could mean:
              <ul className="mb-0 mt-2">
                <li>No one has registered for this event in KP Leagues yet</li>
                <li>The round_id ({roundId}) might be incorrect</li>
                <li>There might be a temporary API issue</li>
              </ul>
            </Alert>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <Table striped bordered hover responsive="sm" size="sm">
                <thead>
                  <tr>
                    <th>Player Name</th>
                    <th>Email</th>
                    <th>Player ID</th>
                    <th>User ID</th>
                    <th>Event Entries ID</th>
                    <th>Round Detail ID</th>
                    <th>Player Score</th>
                  </tr>
                </thead>
                <tbody>
                  {registrants.map((registrant, index) => (
                    <tr key={registrant.player_id || index}>
                      <td>{registrant.player_name || "N/A"}</td>
                      <td>{registrant.email_address || "N/A"}</td>
                      <td>{registrant.player_id || "N/A"}</td>
                      <td>
                        {registrant.user_id ? (
                          <span className="text-success">
                            ✅ {registrant.user_id}
                          </span>
                        ) : (
                          <span className="text-warning">⚠️ Not mapped</span>
                        )}
                      </td>
                      <td>
                        {registrant.event_entries_id ? (
                          <span className="text-success">
                            ✅ {registrant.event_entries_id}
                          </span>
                        ) : (
                          <span className="text-warning">⚠️ Not mapped</span>
                        )}
                      </td>
                      <td>{registrant.round_detail_id || "N/A"}</td>
                      <td>{registrant.player_score || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Manual Mapping Modal */}
      {showManualMapping && selectedPlayer && (
        <Modal
          show={showManualMapping}
          onHide={() => {
            setShowManualMapping(false);
            setSelectedPlayer(null);
          }}
          size="lg"
        >
          <Modal.Header>
            <Modal.Title>
              Map Player: {selectedPlayer.users_public?.name}
            </Modal.Title>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                setShowManualMapping(false);
                setSelectedPlayer(null);
              }}
              className="btn-close"
              aria-label="Close"
            >
              ✕
            </Button>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <p>
                <strong>Scorecard Player Info:</strong>
              </p>
              <ul>
                <li>
                  <strong>Name:</strong> {selectedPlayer.users_public?.name}
                </li>
                <li>
                  <strong>Email:</strong> {selectedPlayer.users?.email}
                </li>
                <li>
                  <strong>User ID:</strong> {selectedPlayer.user_id}
                </li>
              </ul>
            </div>

            <div className="mb-3">
              <h5>Select Matching KP Leagues Registrant:</h5>
              <p>
                Choose which KP Leagues registrant corresponds to this scorecard
                player:
              </p>

              {(() => {
                // Get truly unmapped registrants (not mapped to ANY user)
                const unmappedRegistrants = registrants.filter(
                  (r) => !r.user_id
                );

                // Also check if any local players are already mapped to these KP players
                const availableRegistrants = unmappedRegistrants.filter((r) => {
                  // Make sure no other local player is mapped to this KP player
                  const isKpPlayerMappedElsewhere = items?.some(
                    (localPlayer) =>
                      localPlayer.users_public?.player_id === r.player_id &&
                      localPlayer.user_id !== selectedPlayer.user_id
                  );
                  return !isKpPlayerMappedElsewhere;
                });

                if (availableRegistrants.length === 0) {
                  return (
                    <Alert variant="warning">
                      <strong>No available registrants for mapping</strong>
                      <br />
                      {unmappedRegistrants.length === 0
                        ? "All KP Leagues registrants have already been mapped to users."
                        : "All unmapped KP Leagues registrants are already mapped to other scorecard players."}
                      <br />
                      <small>
                        Try refreshing the data to see the latest mapping
                        status.
                      </small>
                    </Alert>
                  );
                }

                // Sort available registrants by name similarity
                const sortedRegistrants = getSortedRegistrantsByNameMatch(
                  selectedPlayer.users_public?.name,
                  availableRegistrants
                );

                const getBadgeVariant = (matchType) => {
                  switch (matchType) {
                    case "exact":
                      return "success";
                    case "all_parts":
                      return "primary";
                    case "reverse_all_parts":
                      return "info";
                    case "partial":
                      return "warning";
                    default:
                      return "secondary";
                  }
                };

                const getMatchLabel = (matchType) => {
                  switch (matchType) {
                    case "exact":
                      return "Exact Match";
                    case "all_parts":
                      return "Strong Match";
                    case "reverse_all_parts":
                      return "Good Match";
                    case "partial":
                      return "Partial Match";
                    default:
                      return "";
                  }
                };

                return (
                  <Table striped bordered hover size="sm">
                    <thead>
                      <tr>
                        <th>Player Name</th>
                        <th>Email</th>
                        <th>Player ID</th>
                        <th>Match Quality</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRegistrants.map((registrant, index) => (
                        <tr
                          key={registrant.player_id || index}
                          className={
                            registrant.matchScore > 50 ? "table-success" : ""
                          }
                        >
                          <td>
                            <strong>{registrant.player_name || "N/A"}</strong>
                          </td>
                          <td>{registrant.email_address || "N/A"}</td>
                          <td>{registrant.player_id || "N/A"}</td>
                          <td>
                            {registrant.matchScore > 0 && (
                              <span
                                className={`badge badge-${getBadgeVariant(
                                  registrant.matchType
                                )}`}
                              >
                                {getMatchLabel(registrant.matchType)}
                              </span>
                            )}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant={
                                registrant.matchScore > 80
                                  ? "success"
                                  : "outline-success"
                              }
                              onClick={() =>
                                manualMapPlayer(selectedPlayer, registrant)
                              }
                              disabled={mappingInProgress}
                            >
                              {mappingInProgress
                                ? "Mapping..."
                                : registrant.matchScore > 80
                                ? "✨ Map This Player"
                                : "Map This Player"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                );
              })()}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowManualMapping(false);
                setSelectedPlayer(null);
              }}
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default EventManageRegistrants;
